import Banner from "common/Banner/Banner";
import Link from "common/Typography/Link";
import React, { useEffect, useState } from "react";
import { localStorageService } from "utils/localStorageService";
import styled from "styled-components";
import Title from "common/Typography/Title";
import {
  BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH,
  LARGE_MOBILE,
  MOBILE_FILTERS,
  MOBILE_SEARCH,
} from "common/constants";
import { useHistory } from "react-router-dom";
import { hexToRGB } from "@shared/utils/misc";
import useMedia from "utils/hooks/useMedia";
import Flex from "common/Boxes/Flex";
import Countdown from "common/Countdown/Countdown";
import { getUtcDate } from "@shared/utils/date";
import { getIsDarkMode } from "utils/misc";

const StyledBanner = () => {
  const history = useHistory();
  const isDarkMode = getIsDarkMode();
  const isSmallScreen = useMedia(
    [`(max-width: ${MOBILE_FILTERS})`],
    [true],
    false
  );
  const isMobile = useMedia([`(max-width: ${MOBILE_SEARCH})`], [true], false);
  const decorationPosition = useMedia(
    [`(max-width: 1300px)`, `(max-width: 1500px)`],
    [20, 60],
    100
  );

  const promoStartDate = getUtcDate(2025, 3, 27, 11, 0, 0);
  const promoEndDate = getUtcDate(2025, 4, 3, 23, 59, 59);

  const getShouldHideBanner = () => {
    const currentDate = new Date().getTime();
    return promoStartDate > currentDate || promoEndDate < currentDate;
  };

  const [hideBanner, setHideBanner] = useState(
    localStorageService.getParsedValue<number>(BANNER_KEY) ||
      getShouldHideBanner() ||
      false
  );

  useEffect(() => {
    const displayBannerInterval = setInterval(() => {
      if (
        promoEndDate < new Date().getTime() ||
        localStorageService.getParsedValue<number>(BANNER_KEY)
      ) {
        clearInterval(displayBannerInterval);
      } else {
        setHideBanner(getShouldHideBanner());
      }
    }, 10000);
    return () => clearInterval(displayBannerInterval);
  }, []);

  const onCloseClick = () => {
    localStorageService.setValue(BANNER_KEY, 1);
    setHideBanner(1);
  };

  const getBuyNowLink = () => {
    return history.push(
      `${BASE_PURCHASE_SUBSCRIPTION_SETTINGS_PATH}?all-plans=1&promo_code=${PROMO_CODE}`
    );
  };

  const banner = (
    <StyledBannerWrapper
      $isDarkMode={isDarkMode}
      onCloseClick={onCloseClick}
      style={{ overflow: "visible" }}
    >
      <Flex
        $justifyContent="center"
        $flexDirection={isMobile ? "column" : "row"}
        style={{ gridGap: isSmallScreen ? 12 : 24 }}
      >
        <StyledTitle>
          Use the code
          <PromoCodeWrapper onClick={getBuyNowLink}>
            <PromoCode>{PROMO_CODE}</PromoCode>
          </PromoCodeWrapper>
          to enjoy up to <Discount>65% OFF</Discount> with Cornix annual
          packages!
          <StyledLink onClick={getBuyNowLink} $isDarkMode={isDarkMode}>
            BUY NOW
          </StyledLink>
        </StyledTitle>
        <Countdown
          targetDate={promoEndDate}
          onCountdownOver={() => setHideBanner(true)}
        />
      </Flex>
    </StyledBannerWrapper>
  );

  return hideBanner ? null : banner;
};

export default StyledBanner;

const PROMO_CODE = "SPRING2025";
const BANNER_KEY = "sprint25";

const StyledBannerWrapper = styled(Banner)<{ $isDarkMode?: boolean }>`
  background: ${(p) =>
    p.theme.isDarkMode
      ? "linear-gradient(90deg, #1f2323 0%, #293148 100%)"
      : "#EDF1FF"};
  color: ${(p) => p.theme.colors.basic};
  height: 75px;
  text-align: center;
  transition: all 0.15s linear;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  overflow: hidden;
  width: 100%;
  padding: 0 54px;
  user-select: none;
  @media (max-width: 1200px) {
    padding: 0 32px;
  }
  @media (max-width: 600px) {
    padding: 12px 24px;
    height: auto;
  }
  @media (max-width: 450px) {
    padding: 12px 16px;
  }
  @media (max-width: 400px) {
    padding: 12px 12px;
  }
`;
const StyledTitle = styled(Title).attrs({ level: 4 })`
  color: ${(p) => p.theme.colors.basic};
  line-height: 28px;
  z-index: 1;
  @media (max-width: ${LARGE_MOBILE}) {
    line-height: 20px;
  }
`;
const PromoCode = styled(Title).attrs({ level: 3, as: "span" })`
  position: relative;
  color: ${(p) => p.theme.colors.basic};
  font-weight: ${(p) => p.theme.typography.fontWeights.BOLD};
  z-index: 1;
  padding: 0 8px;
  margin: 0 4px;
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode
        ? p.theme.colors.hardBorderGrey
        : hexToRGB("#CACED680", 0.5)};
  border-radius: 4px;
  @media (max-width: ${LARGE_MOBILE}) {
    font-size: ${(p) => p.theme.typography.fontSizes.S};
  }
`;
const PromoCodeWrapper = styled.span`
  cursor: pointer;
  margin: 4px;
  display: inline-flex;
  align-items: center;
`;
const StyledLink = styled(Link)<{ $isDarkMode?: boolean }>`
  color: ${(p) => (p.$isDarkMode ? "#25DDFF" : "#77a2ff")};
  font-weight: 900;
  margin-left: 8px;
  &:hover {
    color: ${(p) => hexToRGB(p.$isDarkMode ? "#25DDFF" : "#77a2ff", 0.8)};
  }
  @media (max-width: ${LARGE_MOBILE}) {
    margin-left: 6px;
    font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  }
`;
const Discount = styled.span`
  font-weight: 900;
  @media (max-width: ${LARGE_MOBILE}) {
    font-weight: ${(p) => p.theme.typography.fontWeights.EXTRA_BOLD};
  }
`;
